import { Injectable } from '@angular/core';
import { UpdateBasketItemSource } from '@epicuro-next/data/baskets-dto';
import {
  ScannerConfigurationKeys,
  ScannerConfigurations,
  SCANNER_RULE_ENGINE_FEATURE_NAME,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  BasketUpdateShops,
  selectActiveBasketId,
  selectBasketItemsWithProductId,
  selectExtendedBasketItems,
  selectExtendedDeliveryShops,
  selectIsOperableStatus,
} from '@epicuro-next/state/basket-state';
import { UBCMessageName } from '@epicuro-next/state/ubc-communication/core';
import {
  updateBasket,
  updateBasketError,
  updateBasketSuccess,
} from '@epicuro-next/state/ubc-communication/ubc-basket';
import { getDeliveryShopId } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { BasketUpdateItem } from 'graphql-api/baskets-api-interfaces';
import { OrderType, ProductKey } from 'graphql-api/gql-api-interfaces';
import {
  filter,
  map,
  pairwise,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

import {
  addToBasket,
  addToBasketFail,
  addToBasketSuccess,
  shopScannedProductToBasket,
} from '../barcode-reader';
import {
  ProductByBarcode,
  ScannerStatus,
  UpdateBasketItemParams,
} from '../models';

import {
  clearScannedProducts,
  setBasketScannedProductEntry,
  syncScannedProductsQuantity,
  triggerSyncScannedProductsQuantity,
  updateScannedProductQuantity,
  updateScannedProductQuantityFailed,
  updateScannedProductQuantitySuccess,
} from './scan-to-basket.actions';
import { selectScannedBasketProducts } from './scan-to-basket.selectors';

@Injectable()
export class ScanToBasketEffects {
  constructor(private actions$: Actions, private store: Store) {}
  private isSyncQuantityEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ScannerConfigurationKeys,
        ScannerConfigurations
      >(
        SCANNER_RULE_ENGINE_FEATURE_NAME,
        ScannerConfigurationKeys.SyncScannedBasketProductsQuantity,
        false,
      ),
    ),
  );

  public clearScannedProductsOnCreateSwitchBasket$ = createEffect(() =>
    this.store.pipe(
      select(selectActiveBasketId),
      pairwise(),
      filter(([prev, curr]) => {
        return !!prev && prev !== curr;
      }),
      map(() => clearScannedProducts()),
    ),
  );

  public addToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addToBasket),
      concatLatestFrom((action) =>
        this.store.pipe(
          select(selectBasketItemsWithProductId(action.product.productId)),
        ),
      ),
      map(([action, existingBasketItems]) => {
        const product = action.product;
        const parent = product.parent;
        const grandParent = parent?.parent;
        if (!product.canBeSold) {
          return addToBasketFail({
            ...action,
            reason: ScannerStatus.ERROR_ADD_BASKET_NOT_SELLABLE,
          });
        }
        if (product.isLocked || parent?.isLocked || grandParent?.isLocked) {
          return addToBasketFail({
            ...action,
            reason: ScannerStatus.ERROR_ADD_BASKET_LOCKED,
          });
        }
        if (existingBasketItems.length > 0) {
          return addToBasketFail({
            ...action,
            reason: ScannerStatus.ERROR_ADD_BASKET_ALREADY_IN,
          });
        }

        const params = updateBasketParameters(product);

        if (!params) {
          return addToBasketFail({
            ...action,
            reason: ScannerStatus.ERROR_ADD_BASKET_NOT_AVAILABLE,
          });
        }

        return shopScannedProductToBasket({
          barcode: action.barcode,
          product: action.product,
          quantity: 1,
          updateBasketItemParams: params,
          mode: 'add',
        });
      }),
    ),
  );

  public shopScannedProductToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(shopScannedProductToBasket),
      concatLatestFrom(() =>
        this.store.pipe(select(selectExtendedDeliveryShops)),
      ),
      map(([{ product, updateBasketItemParams, quantity }, shops]) => {
        const updateShops: BasketUpdateShops = shops.reduce((acc, shop) => {
          return {
            ...acc,
            [getDeliveryShopId(shop)]: [
              productToBasketItem(
                product,
                quantity,
                updateBasketItemParams,
                null,
              ),
            ],
          };
        }, {});

        return updateBasket({
          messageName: UBCMessageName.UpdateBasket,
          payload: {
            shops: updateShops,
            initiatorSource: UBCMessageName.ScanToBasket,
            updateStrategy: 'overwrite',
          },
        });
      }),
    ),
  );

  public handleBasketUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(shopScannedProductToBasket),
      switchMap((action) =>
        this.actions$.pipe(
          ofType(updateBasketSuccess),
          filter(
            ({ payload }) => payload.source === UBCMessageName.ScanToBasket,
          ),
          take(1),
          takeUntil(this.actions$.pipe(ofType(updateBasketError))),
          map(() => action),
        ),
      ),
      switchMap(
        ({ barcode, product, quantity, updateBasketItemParams, mode }) => {
          const scannedBasketProduct = {
            ...product,
            quantity,
            ...updateBasketItemParams,
          };
          return [
            setBasketScannedProductEntry({
              scannedBasketProduct,
            }),
            mode === 'add'
              ? addToBasketSuccess({
                  barcode: barcode as string,
                  product,
                })
              : updateScannedProductQuantitySuccess({
                  scannedBasketProduct,
                }),
          ];
        },
      ),
    ),
  );

  public handleBasketUpdateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(shopScannedProductToBasket),
      switchMap((action) =>
        this.actions$.pipe(
          ofType(updateBasketError),
          filter(
            ({ payload }) => payload.source === UBCMessageName.ScanToBasket,
          ),
          take(1),
          takeUntil(this.actions$.pipe(ofType(updateBasketSuccess))),
          map(() => action),
        ),
      ),
      map(({ barcode, product, mode }) =>
        mode === 'add'
          ? addToBasketFail({
              barcode: barcode as string,
              product,
            })
          : updateScannedProductQuantityFailed({
              scannedBasketProductId: product.productId,
            }),
      ),
    ),
  );

  public updateScannedProductQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateScannedProductQuantity),
      map((action) => {
        const { scannedBasketProduct, quantity } = action;
        // unnecessary update action
        if (scannedBasketProduct.quantity === quantity) {
          return updateScannedProductQuantitySuccess({
            scannedBasketProduct,
          });
        }

        const {
          requestedDate,
          orderType,
          inventoryId,
          basketItemIds,
          ...product
        } = scannedBasketProduct;
        return shopScannedProductToBasket({
          product,
          quantity,
          mode: 'update',
          updateBasketItemParams: {
            requestedDate,
            orderType,
            inventoryId,
          },
        });
      }),
    ),
  );

  public triggerSyncScannedProductsQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerSyncScannedProductsQuantity),
      withLatestFrom(this.isSyncQuantityEnabled$),
      filter(([_, isSyncEnabled]) => !!isSyncEnabled),
      switchMap(() =>
        // wait until update finished
        this.store.pipe(
          select(selectIsOperableStatus),
          filter(Boolean),
          take(1),
        ),
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectScannedBasketProducts)),
        this.store.pipe(select(selectExtendedBasketItems)),
      ]),
      filter(([_, scannedProducts]) => !!scannedProducts.length),
      map(([_, scannedProducts, basketItems]) =>
        syncScannedProductsQuantity({ scannedProducts, basketItems }),
      ),
    ),
  );
}

function updateBasketParameters(
  product: ProductByBarcode,
): UpdateBasketItemParams | undefined {
  if (!product.inventoryItems || product.inventoryItems.length === 0) {
    return undefined;
  }

  const now = new Date();
  const periods = product.inventoryItems
    .map((i) => ({
      from: new Date(i.availableFromDateUtc),
      to: new Date(i.availableToDateUtc),
      orderType: i.orderType,
      inventoryId: i.inventoryId,
    }))
    .filter((p) => p.to > now)
    .sort((a, b) => a.from.getTime() - b.from.getTime());
  const result = periods[0];

  if (!result.from) {
    return {
      requestedDate: now.toISOString(),
      orderType: OrderType.Now,
      inventoryId: result.inventoryId,
    };
  }

  return {
    requestedDate: result.from.toISOString(),
    orderType:
      result.orderType ??
      (result.from < now ? OrderType.Now : OrderType.Future),
    inventoryId: result.inventoryId,
  };
}

export function productToBasketItem(
  product: ProductKey,
  quantity: number = 1,
  updateBasketItemParams: UpdateBasketItemParams,
  basketItemId?: string | null,
): BasketUpdateItem {
  const { productId, brandId } = product;
  return {
    basketItemId,
    productId,
    brandId,
    requestedQuantity: quantity,
    requestedDeliveryDate: updateBasketItemParams.requestedDate,
    orderType: updateBasketItemParams.orderType,
    inventoryId: updateBasketItemParams.inventoryId,
    source: {
      type: UpdateBasketItemSource.Scanner,
    },
  };
}
